import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, Check, MapPin } from "lucide-react";
import { Meta, Schema, Breadcrumbs, buildTitle, buildDescription } from "../components/SEO";
import { findService, services } from "../data/services";
import { cities } from "../data/cities";
import { usePublicProjects } from "../hooks/useProjects";
import { ReviewsSection, ProcessSection, WhyChoose, CtaStrip } from "../components/Sections";
import { QuoteForm } from "../components/QuoteForm";
import { FaqAccordion } from "../components/FaqAccordion";
import { ServiceRichContent } from "../components/ServiceRichContent";
import { ConfiguratorCTA } from "../components/ConfiguratorCTA";
import { SmartImage } from "../components/SmartImage";
import { getContent } from "../data/serviceContent";
import { articlesForService } from "../data/kennisbank";
import { ServicePhotoGallery } from "../components/ServicePhotoGallery";

export default function ServicePage() {
  const { slug } = useParams();
  const service = findService(slug);
  const { allProjects } = usePublicProjects();
  if (!service) return <Navigate to="/diensten" replace />;

  const related = allProjects.filter((p) => p.service_slug === service.slug).slice(0, 3);
  const fallbackProjects = allProjects.slice(0, 3);
  const projectsToShow = related.length ? related : fallbackProjects;
  const richContent = getContent(service.slug);
  const showConfigurator = ["aanbouw", "uitbouw"].includes(service.slug);
  const articles = articlesForService(service.slug);

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.title,
    serviceType: service.title,
    provider: { "@type": "GeneralContractor", name: "Koning Aannemersbedrijf" },
    areaServed: cities.map((c) => c.name),
  };

  return (
    <>
      <Meta
        title={buildTitle(service.seoTitle || `${service.title} Zuid-Holland`)}
        description={buildDescription(service.seoDescription || `${service.title} in Zuid-Holland — ${service.short}. Vaste prijs, vast uitvoeringsteam en een vrijblijvende offerte.`)}
      />
      <Schema data={serviceSchema} />
      <Breadcrumbs items={[
        { name: "Home", url: "/" },
        { name: "Diensten", url: "/diensten" },
        { name: service.title, url: `/diensten/${service.slug}` },
      ]} />

      {/* HERO */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden grain" data-testid="service-hero">
        <SmartImage src={service.image} alt={service.title} sizes="100vw" eager className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative w-full max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20 py-16 md:py-24">
          <div className="max-w-3xl">
            <div className="text-xs tracking-[0.28em] uppercase text-koning-gold mb-4">
              <Link to="/diensten" className="hover:underline">Diensten</Link> · {service.title}
            </div>
            <h1 className="font-display text-white text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
              {service.title}
            </h1>
            <p className="mt-6 text-koning-text-2/90 max-w-xl leading-relaxed">{service.intro}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/offerte" className="btn-gold" data-testid="service-cta-offerte">Vraag offerte aan <ArrowUpRight size={16} /></Link>
              <Link to="/projecten" className="btn-outline">Bekijk projecten</Link>
            </div>
            <div className="mt-8 inline-flex items-center gap-3 bg-koning-bg/60 border border-koning-gold/40 px-5 py-3">
              <span className="text-xs tracking-[0.22em] uppercase text-koning-muted">Prijs</span>
              <span className="font-display text-koning-gold text-lg">Offerte op maat</span>
            </div>
          </div>
        </div>
      </section>

      {/* RECENT PROJECTS — direct onder de hero: klanten kijken eerst naar foto's */}
      {projectsToShow.length > 0 && (
      <section className="py-16 md:py-24 bg-[#0B0B0B] border-y border-koning-border" data-testid="service-projects">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <div className="eyebrow mb-3">Projecten</div>
              <h2 className="font-display text-3xl md:text-5xl text-white tracking-tight">Recent gerealiseerd</h2>
              <p className="mt-3 text-koning-muted max-w-xl">Echte projecten van ons eigen team — klik op een project voor alle foto's en de vóór/ná-vergelijking.</p>
            </div>
            <Link to="/projecten" className="btn-outline">Alle projecten <ArrowUpRight size={16} /></Link>
          </div>
          <div className={`grid grid-cols-1 gap-6 ${projectsToShow.length >= 3 ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
            {projectsToShow.map((p) => (
              <Link key={p.slug} to={`/projecten/${p.slug}`} className="group img-zoom block border border-koning-border hover:border-koning-gold/60 transition-colors" data-testid={`service-project-${p.slug}`}>
                <div className="relative aspect-[4/5]">
                  <SmartImage src={p.cover} alt={p.title} aspect="4/5" sizes="(max-width: 768px) 100vw, 33vw" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-koning-bg/95 via-koning-bg/30 to-transparent" />
                  <div className="absolute inset-0 p-6 flex flex-col justify-end">
                    <div className="text-koning-gold text-[10px] tracking-[0.24em] uppercase">{p.category}</div>
                    <h3 className="font-display text-white text-xl mt-2">{p.title}</h3>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* INTRO + BULLETS + FORM — premium licht voor leesbaarheid */}
      <section className="py-20 md:py-28 bg-[#F5F2EA] border-b border-[#E1D9C5]" data-testid="service-intro">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20 grid lg:grid-cols-[1.2fr_1fr] gap-14">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#8A7430] mb-4">Wat we leveren</div>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight max-w-2xl text-[#1A1816]">
              {service.title.toLowerCase().includes("renovatie")
                ? `Een ${service.title.toLowerCase()} zonder zorgen.`
                : `${service.title} — vakwerk van begin tot eind.`}
            </h2>
            <ul className="mt-8 space-y-4">
              {service.bullets?.map((b, i) => (
                <li key={i} className="flex items-start gap-4 border-b border-[#E1D9C5] pb-4">
                  <Check size={20} className="text-[#8A7430] mt-1 flex-shrink-0" />
                  <span className="text-[#1A1816] text-base md:text-lg">{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 p-6 bg-white border border-[#E1D9C5] shadow-premium">
              <div className="text-xs tracking-[0.24em] uppercase text-[#8A7430] mb-3">Prijs & offerte</div>
              <p className="text-[#1A1816] text-base md:text-lg leading-relaxed">
                <span className="font-display text-2xl text-[#8A7430]">Offerte op maat.</span>{" "}
                <span className="text-[#6F6A60]">De prijs hangt af van maatvoering, materialen en de bestaande staat van uw woning. Vraag een vrijblijvende prijsindicatie aan — u ontvangt binnen één werkdag persoonlijk contact.</span>
              </p>
            </div>
          </div>

          <div className="lg:sticky lg:top-24 self-start">
            <QuoteForm initialService={service.title} compact />
          </div>
        </div>
      </section>

      <ServicePhotoGallery serviceSlug={service.slug} serviceTitle={service.title} />

      <ProcessSection items={service.process} />

      {showConfigurator && <ConfiguratorCTA />}

      <WhyChoose variant="light" items={service.why} />

      {/* RICH SEO CONTENT — 1500+ words for ranking */}
      <ServiceRichContent content={richContent} />

      {/* CITY LINKS — licht beige voor SEO-leesbaarheid */}
      <section className="py-20 md:py-28 bg-[#F5F2EA] border-b border-[#E1D9C5]" data-testid="service-cities">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#8A7430] mb-4">Werkgebied</div>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight max-w-3xl text-[#1A1816]">
            {service.title} in uw stad
          </h2>
          <p className="mt-4 text-[#6F6A60] max-w-2xl leading-relaxed">
            Wij voeren {service.title.toLowerCase()} projecten uit in heel Zuid-Holland. Bekijk per stad onze ervaring en projecten.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-10">
            {cities.map((c) => (
              <Link
                key={c.slug}
                to={`/diensten/${service.slug}/${c.slug}`}
                className="group bg-white border border-[#E1D9C5] p-5 hover:border-[#8A7430] transition-colors shadow-premium"
                data-testid={`service-city-${c.slug}`}
              >
                <div className="flex items-center gap-2 text-[#8A7430] text-[10px] tracking-[0.24em] uppercase">
                  <MapPin size={11} /> Stad
                </div>
                <div className="font-display text-[#1A1816] text-xl mt-2 group-hover:text-[#8A7430] transition-colors">{c.name}</div>
                <div className="text-[#6F6A60] text-xs mt-1">{service.title.toLowerCase()}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <ReviewsSection limit={3} title="Wat klanten zeggen" variant="elevated" />

      {/* FAQ — licht beige */}
      <section className="py-20 md:py-28 bg-[#F5F2EA] border-y border-[#E1D9C5]" data-testid="service-faq">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#8A7430] mb-4">Veelgestelde vragen</div>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight mb-10 text-[#1A1816]">Over {service.title.toLowerCase()}</h2>
          <FaqAccordion items={service.faqs} variant="light" />
        </div>
      </section>

      {articles.length > 0 && (
        <section className="py-16 md:py-20 border-t border-koning-border" data-testid="service-articles">
          <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
            <div className="eyebrow mb-3">Kennisbank</div>
            <h2 className="font-display text-2xl md:text-4xl text-white tracking-tight">Verder lezen over {service.title.toLowerCase()}</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
              {articles.map((a) => (
                <Link key={a.slug} to={`/kennisbank/${a.slug}`} className="group block border border-koning-border p-5 hover:border-koning-gold/60 transition-colors" data-testid={`service-article-${a.slug}`}>
                  <h3 className="font-display text-white text-lg group-hover:text-koning-gold transition-colors">{a.title}</h3>
                  <p className="text-koning-muted text-sm mt-2 line-clamp-3">{a.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CtaStrip
        variant="gold"
        title={`Klaar voor uw ${service.title.toLowerCase()}?`}
        subtitle="Vraag een vrijblijvende prijsindicatie aan — u ontvangt binnen één werkdag persoonlijk contact. Vaste prijs, zonder verrassingen."
      />

      {/* RELATED SERVICES */}
      <section className="py-12 border-t border-koning-border" data-testid="related-services">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-xs tracking-[0.24em] uppercase text-koning-muted mb-4">Bekijk ook</div>
          <div className="flex flex-wrap gap-2">
            {services.filter((s) => s.category === service.category && s.slug !== service.slug).slice(0, 6).map((s) => (
              <Link key={s.slug} to={`/diensten/${s.slug}`} className="px-4 py-2 border border-koning-border text-sm text-koning-muted hover:text-koning-gold hover:border-koning-gold transition-colors">{s.title}</Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
